/**
 * Сколько мягкого фона показывать под текстом — по яркости самого кадра.
 *
 * Фон страницы тайтла и подложка разделов должны читаться (силуэты, свет,
 * цвет — как в приложении), но текст поверх них обязан оставаться читаемым:
 * контраст основного текста с фоном — не ниже 4,5 : 1. Одна и та же доля
 * кадра этого не даёт: тёмному кадру можно показаться почти целиком, а
 * светлое небо при той же доле съедает описание. Поэтому доля считается от
 * светлых мест кадра: чем они ярче, тем сильнее кадр притушен.
 *
 * Мерка — яркость (0–255, как видит глаз), выше которой лежит десятая часть
 * точек готового размытого кадра. Размытие здесь ни при чём: это ровная
 * прозрачность слоя, а не его резкость.
 */

/** Доля кадра у тёмных кадров и её нижний предел у самых светлых. */
export const WASH_VEIL_MAX = 0.9;
export const WASH_VEIL_MIN = 0.45;
/**
 * Какой яркости светлые места кадра могут быть ПОД текстом: серый 90 из 255
 * даёт с цветом текста страницы контраст 4,5 : 1 с небольшим запасом.
 */
export const WASH_TEXT_CEILING = 90;

/** Доля кадра (прозрачность слоя) по яркости его светлых мест. */
export function washVeil(highlight: number): number {
  if (!(highlight > 0)) return WASH_VEIL_MAX;
  return Math.max(WASH_VEIL_MIN, Math.min(WASH_VEIL_MAX, WASH_TEXT_CEILING / highlight));
}

/** Яркость, выше которой — десятая часть точек (значения RGBA подряд). */
export function highlightOf(pixels: Uint8ClampedArray | readonly number[]): number {
  const histogram = new Uint32Array(256);
  let count = 0;
  for (let i = 0; i + 2 < pixels.length; i += 4) {
    const luma = Math.round(0.299 * pixels[i]! + 0.587 * pixels[i + 1]! + 0.114 * pixels[i + 2]!);
    histogram[luma]! += 1;
    count += 1;
  }
  if (count === 0) return 0;
  let above = 0;
  for (let level = 255; level >= 0; level -= 1) {
    above += histogram[level]!;
    if (above * 10 >= count) return level;
  }
  return 0;
}

/** Доля кадра для готового размытого холста; холст не читается — обычная доля. */
export function canvasVeil(canvas: HTMLCanvasElement | null): number | null {
  if (canvas === null || canvas.width === 0 || canvas.height === 0) return null;
  try {
    const context = canvas.getContext('2d');
    if (!context) return null;
    return washVeil(highlightOf(context.getImageData(0, 0, canvas.width, canvas.height).data));
  } catch {
    return null;
  }
}
